import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MobileNavigation } from '../components/MobileNavigation';
import { Plus, MapPin, Clock, Trash, PencilSimple } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';

export function AdminLocations() {
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: 'CA',
    zip_code: '',
    notes: '',
    timezone: 'America/Los_Angeles'
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
    timezone: ''
  });
  
  useEffect(() => {
    loadLocations();
  }, []);
  
  const loadLocations = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/locations`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else {
        toast.error('Failed to load locations');
      }
    } catch (error) {
      console.error('Load locations error:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleEditInputChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const newLocation = await response.json();
        setLocations([...locations, newLocation]);
        setFormData({
          name: '',
          address: '',
          city: '',
          state: 'CA',
          zip_code: '',
          notes: '',
          timezone: 'America/Los_Angeles'
        });
        setShowAddDialog(false);
        toast.success(`${newLocation.name} has been added successfully!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add location');
      }
    } catch (error) {
      console.error('Add location error:', error);
      toast.error('Failed to add location');
    }
  };
  
  const openEditDialog = (location) => {
    setEditingLocation(location);
    setEditFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip_code: location.zip_code || '',
      notes: location.notes || '',
      timezone: location.timezone || 'America/Los_Angeles'
    });
    setShowEditDialog(true);
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/locations/${editingLocation.location_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editFormData)
      });
      
      if (response.ok) {
        const updatedLocation = await response.json();
        setLocations(locations.map(l => 
          l.location_id === editingLocation.location_id ? updatedLocation : l
        ));
        setShowEditDialog(false);
        setEditingLocation(null);
        toast.success('Location updated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update location');
      }
    } catch (error) {
      console.error('Update location error:', error);
      toast.error('Failed to update location');
    }
  };
  
  const handleDeleteLocation = async (location) => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/locations/${location.location_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setLocations(locations.filter(l => l.location_id !== location.location_id));
        toast.success(`${location.name} has been deleted`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete location');
      }
    } catch (error) {
      console.error('Delete location error:', error);
      toast.error('Failed to delete location');
    }
  };
  
  const formatTimezone = (tz) => {
    const timezones = {
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Chicago': 'Central Time (CT)',
      'America/New_York': 'Eastern Time (ET)'
    };
    return timezones[tz] || tz;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-locations">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">LOCATION MANAGEMENT</h1>
              <p className="text-sm text-muted-foreground">
                {locations.length} location{locations.length !== 1 ? 's' : ''} configured
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-location-btn">
                  <Plus size={20} weight="bold" className="mr-2" />
                  Add Location
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">ADD NEW LOCATION</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Location Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      data-testid="location-name-input"
                      placeholder="e.g., Downtown Community Center"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      data-testid="location-address-input"
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        data-testid="location-city-input"
                        placeholder="Los Angeles"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        data-testid="location-state-input"
                        placeholder="CA"
                        maxLength={2}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        required
                        data-testid="location-zip-input"
                        placeholder="90001"
                      />
                    </div>
                    <div>
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={formData.timezone}
                        onValueChange={(value) => setFormData({...formData, timezone: value})}
                      >
                        <SelectTrigger data-testid="location-timezone-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                          <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                          <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                          <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Input
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      data-testid="location-notes-input"
                      placeholder="Parking, entry instructions, etc."
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary" data-testid="save-location-btn">
                      Save Location
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Locations List */}
      <div className="px-4 py-6">
        {locations.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <MapPin size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO LOCATIONS YET</h3>
              <p className="text-muted-foreground mb-4">
                Add your first gym or facility location to start creating sessions.
              </p>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="btn-primary"
                data-testid="empty-state-add-location-btn"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Add Your First Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 stagger-children">
            {locations.map((location) => (
              <Card key={location.location_id} className="bg-card border-border card-hover">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-heading text-lg mb-1">{location.name}</h3>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} weight="duotone" />
                            <span>{location.address}</span>
                          </div>
                          <div className="ml-6">
                            <span>{location.city}, {location.state} {location.zip_code}</span>
                          </div>
                          {location.notes && (
                            <div className="ml-6 text-xs">
                              <span>📝 {location.notes}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 ml-6">
                            <Clock size={16} weight="duotone" />
                            <span>{formatTimezone(location.timezone)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-primary hover:text-primary hover:bg-primary/10"
                          onClick={() => openEditDialog(location)}
                          data-testid={`edit-location-${location.location_id}`}
                        >
                          <PencilSimple size={18} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleDeleteLocation(location)}
                          data-testid={`delete-location-${location.location_id}`}
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Edit Location Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">EDIT LOCATION</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Location Name</Label>
              <Input
                id="edit-name"
                name="name"
                value={editFormData.name}
                onChange={handleEditInputChange}
                required
                data-testid="edit-location-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-address">Street Address</Label>
              <Input
                id="edit-address"
                name="address"
                value={editFormData.address}
                onChange={handleEditInputChange}
                required
                data-testid="edit-location-address-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  name="city"
                  value={editFormData.city}
                  onChange={handleEditInputChange}
                  required
                  data-testid="edit-location-city-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  name="state"
                  value={editFormData.state}
                  onChange={handleEditInputChange}
                  required
                  data-testid="edit-location-state-input"
                  maxLength={2}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-zip_code">ZIP Code</Label>
                <Input
                  id="edit-zip_code"
                  name="zip_code"
                  value={editFormData.zip_code}
                  onChange={handleEditInputChange}
                  required
                  data-testid="edit-location-zip-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-timezone">Timezone</Label>
                <Select
                  value={editFormData.timezone}
                  onValueChange={(value) => setEditFormData({...editFormData, timezone: value})}
                >
                  <SelectTrigger data-testid="edit-location-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-notes">Notes (Optional)</Label>
              <Input
                id="edit-notes"
                name="notes"
                value={editFormData.notes}
                onChange={handleEditInputChange}
                data-testid="edit-location-notes-input"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="update-location-btn">
                Update Location
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
